/**
 * Cache of externalIds that have already been requested
 * Prevents duplicate network requests for the same thumbnail
 * Stored in-memory for the lifetime of the page / app session.
 */
const preloaded = new Set();


/**
 * Preload thumbnails for a list of videos.
 *
 * This helper creates Image objects and assigns their `src` to the
 * thumbnail URL. The browser will then fetch and cache the image so
 * subsequent <img> elements referencing the same URL render instantly.
 *
 * Notes:
 * - Expects each video to include an `externalId` used by the thumbnail
 *   endpoint. Videos missing `externalId` will be skipped.
 * - Uses an in-memory `Set` (`preloaded`) to ensure each externalId is
 *   requested only once per session (idempotent calls).
 * - The function is fire-and-forget: it does not return a promise and
 *   does not surface network errors. This keeps usage simple for UI
 *   components that just want a best-effort prefetch.
 * - The thumbnail base URL is taken from `process.env.REACT_APP_FIREBASE_API_HOST`.
 *   Ensure this env var is present in the runtime environment (or adjust
 *   the helper to accept a configurable base URL if needed).
 *
 * @param {Array<Object>} videos - Array of video objects. Each video
 *   should have an `externalId` property (string).
 */
export const preloadThumbnails = (videos) => {
  if (!Array.isArray(videos) || videos.length === 0) return;

  videos.forEach((video) => {
    // Defensive check: skip missing objects or missing externalId
    const externalId = video?.externalId;
    if (!externalId) return;

    // Only request once per externalId in the current session
    if (preloaded.has(externalId)) return;

    preloaded.add(externalId);

    // Create an Image to trigger browser-level preload/caching
    const img = new Image();
    img.src = `${process.env.REACT_APP_FIREBASE_API_HOST}/api/thumbnails/${externalId}`;
  });
};
